import { FormikTextInput, FieldWrapper } from '@td-client-frontend/common';
import { unionBy } from 'lodash';
import React, { useState, useEffect } from 'react';
import { Permissions } from '../../../@types/Permissions';
import { Permission, Role } from '../../../@types/Roles';
import { useAuth } from '../../../context/AuthContext';
import { FeaturesGrid } from './FeaturesGrid';

export interface RoleFormProps {
  roleValue: Role;
  isLoading: boolean;
  permissions: Permission[] | undefined;
  setValues: any;
  errors: any;
}

export function RoleForm({ roleValue, isLoading, permissions = [], setValues, errors }: RoleFormProps) {
  const { checkPerm } = useAuth();
  const readOnly =
    (!checkPerm(roleValue ? Permissions.RoleWrite : Permissions.RoleCreate) && checkPerm(Permissions.RoleRead)) ||
    (roleValue && !roleValue?.active);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
  const [role, setRole] = useState(roleValue);

  const updateRole = (newRole: string) => {
    setRole({ ...role, roleNm: newRole });
    setValues({ ...role, roleNm: newRole, permissions: rolePermissions });
  };

  const updatePermission = (permission: Permission) => {
    const newPermissions = rolePermissions.map((permEntry: Permission) =>
      permEntry.name === permission.name ? permission : permEntry
    );
    setValues({ ...role, permissions: unionBy(roleValue?.permissions ?? [], newPermissions, 'name') });
  };

  useEffect(() => {
    if (!rolePermissions.length) {
      const roles = unionBy(roleValue?.permissions ?? [], permissions, 'name');
      setRolePermissions(roles);
    }
  }, [permissions, rolePermissions.length, roleValue]);

  useEffect(() => {
    if (!isLoading) {
      setValues({ ...roleValue, permissions: rolePermissions });
    }
  }, [isLoading, rolePermissions, roleValue, setValues]);

  return (
    <div className="w-full h-full px-2 overflow-scroll" data-testid="RoleForm">
      <div className="flex w-full sm:w-1/2" data-testid="newRoleName">
        <FieldWrapper skipError skipLabel>
          <FormikTextInput
            label="Role name"
            value={role?.roleNm || ''}
            isRequired
            name="roleNm"
            type="text"
            placeholder="Please add role name"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRole(e.target.value)}
            disabled={readOnly}
          />
          {!readOnly && <div className="absolute top-0 right-0 pr-4 text-sm font-normal font-effra">* Required</div>}
        </FieldWrapper>
      </div>
      <FeaturesGrid data={rolePermissions} onChange={updatePermission} readOnly={readOnly} />
    </div>
  );
}
